import { useState, useMemo } from 'react';
import { ProductCard } from '../components/ui/ProductCard';
import products from '../data/products.json';
import { ChevronDown, X, Search, Package } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { usePageSEO } from '../hooks/usePageSEO';

const categories = [
  { value: 'all', label: 'All Products', emoji: '🌿' },
  { value: 'package', label: 'Shadi Package', emoji: '🎁' },
  { value: 'majoon', label: 'Majoon', emoji: '🫙' },
  { value: 'tablets', label: 'Tablets', emoji: '💊' },
  { value: 'oil', label: 'Oils', emoji: '🫗' },
  { value: 'safoof', label: 'Safoof', emoji: '🌾' },
];

const sortOptions = [
  { value: 'featured', label: 'Featured' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'rating', label: 'Top Rated' },
  { value: 'discount', label: 'Best Discount' },
  { value: 'newest', label: 'Newest' },
];

export function Shop() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');

  const categoryTitles = {
    all: 'Buy 100% Pure Herbal Medicines & Courses | WellZora Herbal Care',
    package: 'Shadi Packages & Complete Vitality Courses | WellZora',
    majoon: 'Traditional Turkish Majoon Formulations | WellZora Pakistan',
    tablets: 'Natural Herbal Tablets & Vitality Supplements | WellZora',
    oil: 'Pure Herbal Oils & Natural Extracts | WellZora Pakistan',
    safoof: 'Natural Herbal Safoof Formulations | WellZora',
  };

  const seoTitle = categoryTitles[selectedCategory] || categoryTitles.all;
  const seoDescription = `Browse WellZora's premium herbal ${selectedCategory === 'all' ? 'collection' : selectedCategory} across Pakistan. Laboratory tested, 100% natural with fast cash on delivery.`;

  usePageSEO({
    title: seoTitle,
    description: seoDescription,
    canonical: 'https://wellzoraherbalcare.com/shop',
  });

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory !== 'all') {
      result = result.filter(p => p.category === selectedCategory);
    }

    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'discount':
        result.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      case 'newest':
        result.sort((a, b) => {
          if (a.isPackage) return -1;
          if (b.isPackage) return 1;
          return b.id - a.id;
        });
        break;
      default:
        result.sort((a, b) => {
          if (a.isPackage) return -1;
          if (b.isPackage) return 1;
          const aFeatured = a.badges.includes('bestseller') ? 1 : 0;
          const bFeatured = b.badges.includes('bestseller') ? 1 : 0;
          return bFeatured - aFeatured;
        });
    }

    return result;
  }, [selectedCategory, sortBy]);

  // Count per category
  const categoryCounts = useMemo(() => {
    const counts = { all: products.length };
    categories.forEach(cat => {
      if (cat.value !== 'all') {
        counts[cat.value] = products.filter(p => p.category === cat.value).length;
      }
    });
    return counts;
  }, []);

  const { ref: headerRef, inView: headerInView } = useScrollAnimation();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <section className="bg-gradient-to-br from-primary-dark via-primary to-herbal-700 relative overflow-hidden">
        <div className="absolute inset-0 hero-pattern" />
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div
            ref={headerRef}
            className={`scroll-hidden ${headerInView ? 'scroll-visible' : ''}`}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/15 text-white/90 rounded-full text-sm font-medium mb-4">
              <Package className="w-4 h-4" />
              {products.length} Premium Products
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight">
              Our Herbal Collection
            </h1>
            <p className="text-white/70 text-lg max-w-xl">
              Natural formulations, laboratory tested, trusted across Pakistan
            </p>
          </div>
        </div>
      </section>

      {/* Filter Strip */}
      <section className="sticky top-[4rem] md:top-[6.5rem] z-20 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 py-3 overflow-x-auto scrollbar-hide">
            {/* Category pills */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {categories.map((cat) => {
                const count = categoryCounts[cat.value] || 0;
                if (cat.value !== 'all' && count === 0) return null;
                return (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      selectedCategory === cat.value
                        ? 'bg-primary text-white shadow-md shadow-primary/30'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    <span>{cat.emoji}</span>
                    {cat.label}
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ml-0.5 ${
                      selectedCategory === cat.value ? 'bg-white/20' : 'bg-white text-gray-500'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Sort dropdown — pushed to right */}
            <div className="ml-auto flex-shrink-0 relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-3 pr-8 py-2 text-sm border border-gray-200 rounded-xl bg-white text-gray-700 focus:outline-none focus:border-primary cursor-pointer"
                aria-label="Sort products"
              >
                {sortOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Results count & active filter indicator */}
          <div className="flex items-center justify-between pb-2 px-0.5 text-sm text-gray-500">
            <span>
              <span className="font-semibold text-gray-900">{filteredProducts.length}</span> products found
              {selectedCategory !== 'all' && (
                <span> in <span className="text-primary font-medium">{categories.find(c => c.value === selectedCategory)?.label || selectedCategory}</span></span>
              )}
            </span>
            {selectedCategory !== 'all' && (
              <button
                onClick={() => setSelectedCategory('all')}
                className="flex items-center gap-1 text-primary hover:underline text-xs"
              >
                <X className="w-3 h-3" /> Clear filter
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-10 h-10 text-gray-300" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">No products found</h2>
              <p className="text-gray-500 mb-6">Try a different category</p>
              <button
                onClick={() => setSelectedCategory('all')}
                className="text-primary font-semibold hover:underline"
              >
                View all products
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div key={product.id}>
                  <ProductCard product={product} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="py-16 bg-gradient-to-r from-primary-dark to-primary relative overflow-hidden">
        <div className="absolute inset-0 hero-pattern" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need Expert Guidance?</h2>
          <p className="text-white/75 max-w-xl mx-auto mb-8 text-lg">
            Talk to our herbal experts for personalized product recommendations.
          </p>
          <a
            href="https://wa.me/923056367673"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-primary px-8 py-4 rounded-2xl font-bold text-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            <svg className="w-6 h-6 fill-green-500" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.122.554 4.112 1.523 5.84L.057 23.999l6.305-1.654A11.954 11.954 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.885 0-3.643-.518-5.153-1.42l-.369-.22-3.742.981.998-3.648-.24-.376A9.946 9.946 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/>
            </svg>
            Chat on WhatsApp
          </a>
        </div>
      </section>
    </div>
  );
}